import { sendAnalytics } from '../../../../telemetry/client';
import type { QuickstartState } from '../../QuickstartCoordinator';
import type { BaseAnalyticsProps } from '../IQuickstartTelemetryManager';

export function sendSessionStartEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
): void {
  sendAnalytics({
    name: 'Session Start',
    role: 'player',
    persistentId: baseProps.persistentId,
    quickstartSlug: baseProps.quickstartSlug,
    flow: 'quickstart',
    playerAuthId,
    sessionId: state.sessionId,
    page: window.location.pathname,
  });
}

export function sendTrackingPixelFiredEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string | undefined,
  trackerUrl: string,
): void {
  sendAnalytics({
    name: 'Tracking Pixel Fired',
    role: 'player',
    ...baseProps,
    playerAuthId,
    trackerUrl,
  });
}

export function sendEnvironmentCheckCompletedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
): void {
  sendAnalytics({
    name: 'Environment Check Completed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    isEmbeddedBrowser: state.environment.isEmbedded,
    totalDuration,
  });
}

export function sendEmbeddedBrowserBlockedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
): void {
  sendAnalytics({
    name: 'Embedded Browser Blocked',
    role: 'player',
    ...baseProps,
    playerAuthId,
    browserName: state.environment.browserName,
  });
}

export function sendDeviceNotSupportedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  deviceType: 'ios' | 'android' | 'desktop',
  supportedDevices: { ios?: boolean; android?: boolean; desktop?: boolean },
): void {
  sendAnalytics({
    name: 'Device Not Supported',
    role: 'player',
    ...baseProps,
    playerAuthId,
    detectedDeviceType: deviceType,
    supportedIos: supportedDevices.ios !== false,
    supportedAndroid: supportedDevices.android !== false,
    supportedDesktop: supportedDevices.desktop !== false,
    reason: `Campaign does not support ${deviceType} devices`,
  });
}

export function sendOpenInBrowserClickedEvent(
  baseProps: BaseAnalyticsProps,
  playerAuthId: string | undefined,
  browserName: string,
): void {
  sendAnalytics({
    name: 'Open In Browser Clicked',
    role: 'player',
    ...baseProps,
    playerAuthId,
    browserName,
  });
}

export function sendMatchmakingStartedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
  matchmakingTimeout: number,
): void {
  const websocketDuration = state.websocketReadyTime && state.pageLoadTime
    ? state.websocketReadyTime - state.pageLoadTime
    : undefined;

  sendAnalytics({
    name: 'Matchmaking Started',
    role: 'player',
    ...baseProps,
    playerAuthId,
    matchmakingTimeout,
    totalDuration,
    websocketDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
  });
}

export function sendMatchmakingFailedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
  reason: string,
  duration: number,
): void {
  sendAnalytics({
    name: 'Matchmaking Failed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    reason,
    duration,
    totalDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
  });
}

export function sendMatchmakingSucceededEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
): void {
  sendAnalytics({
    name: 'Matchmaking Succeeded',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    duration: state.matchmakingDuration ?? 0,
    totalDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
  });
}

export function sendBroadcastJoinAttemptedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
): void {
  sendAnalytics({
    name: 'Broadcast Join Attempted',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    totalDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
    matchmakingDuration: state.matchmakingDuration,
  });
}

export function sendBroadcastJoinFailedEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
  totalDuration: number | undefined,
  reason: string,
  duration: number,
  errorStage?: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown',
): void {
  sendAnalytics({
    name: 'Broadcast Join Failed',
    role: 'player',
    ...baseProps,
    playerAuthId,
    invitationId: state.matchId ?? '',
    broadcastId: state.broadcastId ?? '',
    reason,
    errorStage,
    duration,
    totalDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
    matchmakingDuration: state.matchmakingDuration,
  });
}

export function sendBroadcastJoinSucceededEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  totalDuration: number | undefined,
  duration: number,
): void {
  sendAnalytics({
    name: 'Player Joined Broadcast',
    role: 'player',
    ...baseProps,
    playerAuthId: localStorage.getItem('clerkUserID') ?? '',
    playerConnectionId: state.playerConnectionId ?? '',
    invitationId: state.matchId ?? '',
    broadcastSessionId: state.broadcastId ?? '',
    broadcastId: state.broadcastId ?? '',
    duration,
    totalDuration,
    lobbyCreationDuration: state.lobbyCreationDuration,
    matchmakingDuration: state.matchmakingDuration,
  });
}

export function sendPlayerLeftBroadcastEvent(
  baseProps: BaseAnalyticsProps,
  state: QuickstartState,
  playerAuthId: string | undefined,
): void {
  sendAnalytics({
    name: 'Player Left Broadcast',
    role: 'player',
    ...baseProps,
    playerAuthId,
    playerConnectionId: state.playerConnectionId ?? '',
    invitationId: state.matchId ?? '',
    broadcastSessionId: state.broadcastId ?? '',
  });
}
